import React, { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Link } from "react-router-dom";
import { useToast } from "./context/toastContext";
import { baseAxios } from "./apis";
import { NavigationBarTestingMode } from "./components/navbar/NavigationBarTestingMode";
import PaperTradingPage from "./components/paper-trading/PaperTradingPage";
import styles from "./styles/Dashboard.module.css";
import Sidebar from "./Sidebar";
import { useSidebar } from "./context/sidebarContext";
import { preloadCoinImages } from "./utils";

// A custom hook to fetch and normalize the list of coins from Binance.
const useBinanceTickerQuery = () => {
  const { addToast } = useToast();
  return useQuery({
    queryKey: ["binanceTicker"],
    queryFn: async () => {
      const BINANCE_URL = "https://api.binance.com/api/v3/ticker/24hr";
      const BACKEND_URL = "/api/binance/ticker/24hr";

      try {
        // Create timeout promise (5 seconds)
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Binance timeout")), 5000)
        );

        // Race between fetch and timeout
        const fetchPromise = axios.get(BINANCE_URL).then((res) => res.data);

        const data = await Promise.race([fetchPromise, timeoutPromise]);
        return data;
      } catch (error) {
        const { data } = await baseAxios.get(BACKEND_URL);
        return data;
      }
    },
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 5,
    select: (data) => {
      const usdtPairs = data
        .filter((coin) => coin.symbol.endsWith("USDT"))
        .sort((a, b) => parseFloat(b.quoteVolume) - parseFloat(a.quoteVolume));

      return usdtPairs.map((coin) => {
        const baseSymbol = coin.symbol.replace("USDT", "").toLowerCase();
        return {
          id: baseSymbol,
          symbol: baseSymbol,
          name: coin.symbol,
          current_price: parseFloat(coin.lastPrice),
          high_24h: parseFloat(coin.highPrice),
          low_24h: parseFloat(coin.lowPrice),
          price_change_24h: parseFloat(coin.priceChange),
          price_change_percentage_24h: parseFloat(coin.priceChangePercent),
          total_volume: parseFloat(coin.quoteVolume),
        };
      });
    },
    onError: (err) => {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to fetch coins";
      addToast("error", errorMessage, 10000);
      console.error(err);
    },
  });
};

// A custom hook for fetching the paper trading summary, dependent on the selected coin.
const usePaperTradingSummaryQuery = (selectedCoin) => {
  return useQuery({
    queryKey: ["paperTradingSummary", selectedCoin?.symbol],
    queryFn: () =>
      baseAxios
        .get(
          `/api/paper_trading/summary?symbol=${selectedCoin.symbol.toUpperCase()}USDT`
        )
        .then((res) => res.data),
    enabled: !!selectedCoin,
  });
};

export const PaperTrading = () => {
  // Store only the symbol of the selected coin in state
  const [selectedCoinSymbol, setSelectedCoinSymbol] = useState(null);
  const { isOpen, toggleSidebar } = useSidebar();

  const { data: coins = [], isSuccess } = useBinanceTickerQuery();

  // On every render, find the full, up-to-date coin object from the latest coins array
  const selectedCoin = useMemo(() => {
    if (!selectedCoinSymbol) return null;
    return coins.find((c) => c.symbol === selectedCoinSymbol);
  }, [coins, selectedCoinSymbol]);

  const { data: summary, isLoading: loadingSummary } =
    usePaperTradingSummaryQuery(selectedCoin);

  // This effect sets the default selected coin symbol once the list has loaded.
  useEffect(() => {
    if (!selectedCoinSymbol && coins.length > 0) {
      setSelectedCoinSymbol(coins[0].symbol);
    }
  }, [coins, selectedCoinSymbol]);

  // Preload coin images when coins data is available
  useEffect(() => {
    if (isSuccess && coins.length > 0) {
      // Preload top 50 coins to avoid overloading the backend
      const topCoins = coins.slice(0, 10).map((coin) => coin.symbol);
      preloadCoinImages(topCoins);
    }
  }, [coins, isSuccess]);

  return (
    <div className={styles.dashboard}>
      <NavigationBarTestingMode />
      <Sidebar isOpen={isOpen} onClose={toggleSidebar} />
      <div className="h-screen overflow-y-auto w-full">
        <div className="container mx-auto max-w-6xl xl:!pt-[11vh] p-5">
          <p className="text-sm font-semibold mt-6">
            <Link to="/testing-mode" className="text-[#0E0F11]">
              Which Tes...
            </Link>
            &nbsp;/ <span className="text-[#0E0F11]/60">Paper Trading</span>
          </p>
          <h1 className="text-2xl md:text-3xl font-bold m-0 mt-6 text-left">
            Paper Trading
          </h1>
          <PaperTradingPage
            summary={summary}
            loadingSummary={loadingSummary}
            coins={coins}
            selectedCoin={selectedCoin}
            setSelectedCoin={(coin) => setSelectedCoinSymbol(coin.symbol)}
          />
        </div>
      </div>
    </div>
  );
};
